// Composants du lot 2 ajoutés au système de design (charte D-038, « exceptions d'abord ») : autonomie en jours
// avec confiance et motif (« indéterminée » explicite), cuve à niveau avec repère de fond de cuve et hachures pour
// une donnée ancienne, badge d'état de validation, pastille de qualité de donnée, tableau de positions par
// propriétaire, étiquette de niveau d'alerte, liste compacte d'alertes. Aucune couleur littérale : jetons du
// thème uniquement ; aucune valeur absente affichée comme 0.

import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { formaterNombre, formaterQuantite, LIBELLES_ETATS, LIBELLES_FRAICHEUR, type EtatValidation, type Fraicheur, type Unite } from '@carburanttrack/shared';
import { LIBELLES_QUALITE, type QualiteDonnee } from '@carburanttrack/shared/matiere';
import { Indisponible } from '../../composants/Etats';
import { Icone } from '../../composants/Icones';
import { dateHeure } from '../../composants/format';
import { useProduits } from '../../api/requetes';
import type { AlerteResume, NiveauAlerte, Position } from './types';
import './stocks.css';

/** Nom d'un produit du catalogue (le code s'affiche tant que le catalogue n'est pas chargé). */
export function useNomProduit() {
  const produits = useProduits();
  return (code: string) => produits.data?.find((p) => p.code === code)?.nom ?? code;
}

/** Quantité lisible : au-delà de 100 000 L, affichage en mètres cubes (simple changement d'échelle). */
/** `reference` : valeur voisine (total du produit) dont l'unité d'affichage s'impose, pour ne pas mêler L et m³ dans un même bloc. */
export function quantite(valeur: string | null | undefined, unite: string, reference?: string | null): string {
  if (valeur === null || valeur === undefined) return 'indisponible';
  if (unite === 'L' && Math.max(Math.abs(Number(valeur)), Math.abs(Number(reference ?? 0))) >= 100_000) return formaterQuantite(String(Number(valeur) / 1000), 'm3', { decimales: 0 });
  return formaterQuantite(valeur, (unite === 'kg' ? 'kg' : unite === 'L' ? 'L' : unite) as Unite);
}

export const LIBELLES_CONFIANCE: Record<string, string> = {
  haute: 'confiance haute',
  moyenne: 'confiance moyenne',
  faible: 'confiance faible',
  indeterminee: 'indéterminée',
};

export interface ProprietesAutonomie {
  jours: string | null;
  confiance: string;
  motif: string;
  joursAvecLivraisons?: string | null;
  /** Ton d'exception tiré d'une alerte ouverte (jamais d'un seuil codé dans l'interface). */
  ton?: 'rouge' | 'ambre' | null;
  compact?: boolean;
}

/** Autonomie en jours avec sa confiance et son motif (D-027) ; « indéterminée » est affichée explicitement. */
export function AutonomieJours({ jours, confiance, motif, joursAvecLivraisons, ton, compact }: ProprietesAutonomie) {
  if (jours === null) {
    return (
      <span className="autonomie indeterminee">
        <b>indéterminée</b>
        <small>{motif}</small>
      </span>
    );
  }
  const n = Number(jours);
  return (
    <span className={`autonomie${ton ? ` ${ton}` : ''}`}>
      <b>
        {formaterNombre(jours, n < 10 ? 1 : 0)} jour{n >= 2 ? 's' : ''}
      </b>
      <small>
        {LIBELLES_CONFIANCE[confiance] ?? confiance}
        {!compact && `, ${motif.charAt(0).toLowerCase()}${motif.slice(1)}`}
      </small>
      {joursAvecLivraisons !== null && joursAvecLivraisons !== undefined && joursAvecLivraisons !== jours && (
        <small>avec les livraisons confirmées : {formaterNombre(joursAvecLivraisons, Number(joursAvecLivraisons) < 10 ? 1 : 0)} jours</small>
      )}
    </span>
  );
}

export interface ProprietesCuveSeuil {
  libelle: string;
  capacite: string;
  unite: string;
  volume: string | null;
  /** Fond de cuve (volume non pompable) : repère tracé sur la cuve. */
  seuil: string | null;
  fraicheur: Fraicheur | null;
  age?: string | null;
  motifIndisponible?: string;
  ton?: 'rouge' | 'ambre' | null;
  mention?: ReactNode;
}

/** Cuve à niveau avec repère du fond de cuve ; hachures pour une donnée ancienne, périmée ou absente. */
export function CuveSeuil({ libelle, capacite, unite, volume, seuil, fraicheur, age, motifIndisponible, ton, mention }: ProprietesCuveSeuil) {
  const cap = Number(capacite);
  const part = volume !== null && cap > 0 ? Math.max(0, Math.min(100, (Number(volume) / cap) * 100)) : null;
  const partSeuil = seuil !== null && cap > 0 ? Math.max(0, Math.min(100, (Number(seuil) / cap) * 100)) : null;
  const ancienne = fraicheur !== null && fraicheur !== 'recente';
  const classe = volume === null ? 'reservoir inconnue' : ancienne ? 'reservoir ancienne' : 'reservoir';
  const description =
    volume === null
      ? `${libelle}, capacité ${quantite(capacite, unite)}, niveau indisponible : ${motifIndisponible ?? 'aucune déclaration'}`
      : `${libelle}, ${quantite(volume, unite)} sur ${quantite(capacite, unite)}${fraicheur ? `, donnée ${LIBELLES_FRAICHEUR[fraicheur].toLowerCase()}` : ''}`;
  return (
    <figure className="cuve cuve-seuil">
      <b>{libelle}</b>
      <div className={classe} role="img" aria-label={description}>
        {part !== null && <div className={`niveau${ton ? ` ${ton}` : ''}`} style={{ height: `${part}%` }} />}
        {partSeuil !== null && <div className="repere-fond" style={{ bottom: `${partSeuil}%` }} title={`Fond de cuve : ${quantite(seuil, unite)}`} />}
        {volume === null && <span className="mention">indisponible</span>}
      </div>
      {volume === null ? <small>{motifIndisponible ?? 'aucune déclaration'}</small> : <div className="valeur">{quantite(volume, unite)}</div>}
      <span className="capacite">sur {quantite(capacite, unite)}</span>
      {fraicheur ? <PastilleFraicheur fraicheur={fraicheur} age={age ?? undefined} /> : null}
      {mention}
    </figure>
  );
}

export function PastilleFraicheur({ fraicheur, age }: { fraicheur: Fraicheur; age?: string }) {
  return (
    <span className={`pastille ${fraicheur}`}>
      {LIBELLES_FRAICHEUR[fraicheur]}
      {age ? `, ${age}` : ''}
    </span>
  );
}

/** Pastille de qualité d'une donnée (C3) : connectée, déclarée, vérifiée, estimée, en attente, périmée. */
export function QualiteDonneeEtiquette({ qualite }: { qualite: string | null }) {
  if (!qualite) return <span className="attenue">indisponible</span>;
  const ton = qualite === 'en_attente' ? ' ambre' : qualite === 'perimee' ? ' gris' : '';
  return <span className={`etiquette${ton}`}>{LIBELLES_QUALITE[qualite as QualiteDonnee] ?? qualite}</span>;
}

/** Badge d'état de validation (page 41 du dossier) : la couleur ne signale que ce qui demande une action. */
export function BadgeValidation({ etat }: { etat: string }) {
  const ton = etat === 'en_attente_validation' || etat === 'conteste' ? ' ambre' : etat === 'annule' || etat === 'archive' ? ' gris' : etat === 'valide' || etat === 'verifie' ? ' vert' : '';
  return <span className={`etiquette${ton}`}>{LIBELLES_ETATS[etat as EtatValidation] ?? etat.replace(/_/g, ' ')}</span>;
}

export const LIBELLES_NIVEAUX: Record<NiveauAlerte, string> = { informative: 'Informative', preventive: 'Préventive', elevee: 'Élevée', critique: 'Critique' };

/** Niveau d'alerte : vigilance (informative, préventive) en ambre, difficulté (élevée, critique) en rouge, avec libellé. */
export function EtiquetteNiveau({ niveau }: { niveau: NiveauAlerte }) {
  return <span className={`etiquette ${niveau === 'critique' || niveau === 'elevee' ? 'rouge' : 'ambre'}`}>{LIBELLES_NIVEAUX[niveau] ?? niveau}</span>;
}

export const tonDeNiveau = (niveau: NiveauAlerte | null | undefined): 'rouge' | 'ambre' | null =>
  !niveau ? null : niveau === 'critique' || niveau === 'elevee' ? 'rouge' : 'ambre';

export function ListeAlertesCompacte({ alertes, vide }: { alertes: AlerteResume[] | null; vide: string }) {
  if (alertes === null) return <Indisponible motif={vide} />;
  if (alertes.length === 0) return <p className="attenue">{vide}</p>;
  return (
    <ul className="liste-alertes-compacte">
      {alertes.map((a) => (
        <li key={a.id}>
          <Link className={`alerte ${a.niveau}`} to={`/alertes/${a.id}`}>
            <i aria-hidden="true" />
            <span>
              <span className="meta">
                <EtiquetteNiveau niveau={a.niveau} />
                <span>{dateHeure(a.derniereDetection)}</span>
              </span>
              <b>{a.titre}</b>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Tableau de positions par propriétaire : comptable, réservé, immobilisé, sécurité, disponible à l'expédition. */
export function TableauPositions({ positions, avecInstallation, avecProprietaire = true, legende }: { positions: Position[]; avecInstallation?: boolean; avecProprietaire?: boolean; legende: string }) {
  const nomProduit = useNomProduit();
  // Une seule unité par colonne : m³ dès qu'une position en litres du tableau atteint 100 000 L.
  const reference = String(Math.max(0, ...positions.filter((p) => p.unite === 'L').map((p) => Math.abs(Number(p.comptable ?? 0)))));
  return (
    <div className="tableau-conteneur">
      <table className="tableau tableau-positions">
        <caption className="visuellement-cache">{legende}</caption>
        <thead>
          <tr>
            {avecInstallation && <th scope="col">Dépôt</th>}
            <th scope="col">Produit</th>
            {avecProprietaire && <th scope="col">Propriétaire</th>}
            <th scope="col" className="nombre">
              Comptable
            </th>
            <th scope="col" className="nombre masquable">
              Réservé
            </th>
            <th scope="col" className="nombre masquable">
              Immobilisé
            </th>
            <th scope="col" className="nombre masquable">
              Sécurité
            </th>
            <th scope="col" className="nombre">
              Disponible à l’expédition
            </th>
          </tr>
        </thead>
        <tbody>
          {positions.map((p) => {
            const negatif = p.disponible !== null && Number(p.disponible) < 0;
            return (
              <tr key={p.cle}>
                {avecInstallation && (
                  <td>
                    <Link className="lien-ligne" to={`/stocks/installations/${p.installationId}`}>
                      {p.installationNom}
                    </Link>
                  </td>
                )}
                <td>{nomProduit(p.produitCode)}</td>
                {avecProprietaire && <td>{p.proprietaireNom}</td>}
                <td className="nombre">{quantite(p.comptable, p.unite, reference)}</td>
                <td className="nombre masquable">{quantite(p.reserve, p.unite, reference)}</td>
                <td className="nombre masquable">{quantite(p.immobilise, p.unite, reference)}</td>
                <td className="nombre masquable">{quantite(p.securite, p.unite, reference)}</td>
                <td className={`nombre${negatif ? ' negatif' : ''}`}>
                  {quantite(p.disponible, p.unite, reference)}
                  {negatif && <small>engagements supérieurs à la position</small>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Ligne d'action de la liste des actions (vraie navigation, jamais un bouton sans traitement). */
export function LienAction({ vers, icone, titre, detail }: { vers: string; icone: Parameters<typeof Icone>[0]['nom']; titre: string; detail: string }) {
  return (
    <li>
      <Link to={vers}>
        <span className="icone">
          <Icone nom={icone} />
        </span>
        <span className="texte">
          <b>{titre}</b>
          <small>{detail}</small>
        </span>
        <Icone nom="chevron" />
      </Link>
    </li>
  );
}

/** Horodatage ISO de l'instant présent, au format attendu par un champ datetime-local (heure du Gabon). */
export function maintenantLocal(): string {
  const d = new Date(Date.now() + 3_600_000);
  return d.toISOString().slice(0, 16);
}

/** Valeur d'un champ datetime-local saisie en heure du Gabon vers un horodatage ISO avec fuseau. */
export function localVersIso(valeur: string): string {
  return `${valeur}:00+01:00`;
}

/** Saisie décimale française (virgule) vers chaîne décimale à point ; null si vide. */
export function decimalSaisi(valeur: string): string | null {
  const t = valeur.replace(/[\s  ]/g, '').replace(',', '.');
  return t === '' ? null : t;
}
